import Label from "@/components/label/label";
import SettingSelectField from "@/components/settingFields/SettingSelectField";
import { useLocale } from "@/contexts/localeContext";
import { TranslationKeys } from "@/contexts/TranslationKeys";
import { IAppState } from "@/store/types";
import { positiveNumbers } from "@/utilities/regex";
import { requiredValidation, validateItemName } from "@/utilities/Validation";
import { Field, useFormikContext } from "formik";
import { FC, useRef } from "react";
import { useSelector } from "react-redux";
import { typeOptions } from "./OssDropDownOptions";

interface DetailsProps {
    onNext: () => void;
}

const Details: FC<DetailsProps> = ({ onNext }) => {
    const { translate } = useLocale();
    const { values, errors, setFieldValue } = useFormikContext<any>();
    const globalSite = useSelector((state: IAppState) => state.site.site.label);
    const globalSiteId = useSelector((state: IAppState) => state.site.site.value);
    const inputRef = useRef<HTMLInputElement>(null);

    const onLocalNext = () => {
        setFieldValue("siteId", globalSiteId);
        setFieldValue("siteName", globalSite);
        onNext();
    }

    const restrictNumericInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const key = e.key;
        const isPositiveDigit = positiveNumbers.test(key);
        // Always allow Backspace
        if (key === 'Backspace' || isPositiveDigit) {
            return;
        }

        e.preventDefault();
    }

    return <div className='flex flex-col h-full px-2 py-1 relative space-y-1'>
        <>
            <span className={`text-portal-blue-primary-shade2 text-xl font-semibold`} >
                {translate.byKey(TranslationKeys.details)}
            </span>
            {/* Name */}
            <div className='flex flex-col'>
                <div className="flex flex-row justify-between" id='nameLabel'>
                    <Label
                        htmlFor="name"
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.name)}:</Label>
                    {errors.name ? <span className='text-red-600 text-xs'>{errors.name}</span> : null}
                </div>

                <div className="flex flex-row justify-between">
                    <Field
                        type="text"
                        name="name"
                        data-cy="nameField"
                        id="nameInput"
                        innerRef={inputRef}
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                        validate={validateItemName} />
                </div>
            </div>
            {/* Type */}
            <div className='flex flex-col'>
                <div className="flex flex-row justify-between" id='ossdTypeLabel'>
                    <Label
                        htmlFor="ossdType"
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.type)}:</Label>
                    {errors.ossdType ? <span className='text-red-600 text-xs'>{errors.ossdType}</span> : null}
                </div>

                <div className="flex flex-row justify-between">
                    <SettingSelectField
                        className="flex flex-row w-full"
                        accessor={`ossdType`}
                        options={typeOptions} />
                </div>
            </div>
            {/* Device Id */}
            <div className='flex flex-col'>
                <div className="flex flex-row justify-between" id='ossdDeviceField'>
                    <Label htmlFor='ossdDeviceId'
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(values.ossdType === "0" ? TranslationKeys.doorGroupId : TranslationKeys.doorId)}:</Label>
                    {errors.ossdDeviceId ? <span className='text-red-600 text-xs'>{errors.ossdDeviceId}</span> : null}
                </div>
                <div className="flex flex-row justify-between">
                    <Field
                        type="number"
                        name="ossdDeviceId"
                        data-cy="ossdDeviceField"
                        id="ossdDeviceInput"
                        innerRef={inputRef}
                        onKeyDown={restrictNumericInput}
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                        validate={requiredValidation} />
                </div>
            </div>
            {/* Site */}
            <div className='flex flex-col'>
                <div className="flex flex-row justify-between" id="siteLabel">
                    <Label
                        htmlFor="siteId"
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.site)}:</Label>
                    {errors.siteId ? <span className='text-red-600 text-xs'>{errors.siteId}</span> : null}
                </div>
                <Field
                    value={globalSite}
                    data-cy="siteField"
                    disabled={true}
                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
            </div>
        </>
        <div className="flex flex-row justify-end absolute bottom-0 right-0 pr-3 pb-3 ">
            <div className="flex flex-row justify-end mt-4 pl-3">
                <button
                    className={`py-1 px-5 secondary ${values.name && values.ossdDeviceId ? 'secondary-enabled' : 'secondary-disabled cursor-not-allowed'}`}
                    type="button"
                    data-cy="nextButton"
                    disabled={!(values.name && values.ossdDeviceId)}
                    onClick={onLocalNext}>{translate.byKey(TranslationKeys.next)}
                </button>
            </div>
        </div>
    </div>
}

export default Details;